import { SRPDModuleInformationInterface, SRPDPreAssemblyImageInterface, StateMachineListInterface, StateSRPdParentLevelInterface } from "../../../models/SRPSchema";
import { useDataContext } from "../../DataProvider/DataContext";
import { useEffect, useState, Dispatch, SetStateAction } from "react";
import { validateMachineList, validateFormAndGetIsValid, validateModuleInformation, validateSRPInformation, softValidateMachineList, softValidateTextFieldValues, softValidateModuleInformation } from "../../../utilities/formValidation";
import {  removeDataFromLocalStorage, saveDataToLocalStorage } from "../../../utilities/localStorageUtils";
import { currentSRPSchemaVersion, READINESS_CHECK_TOOLTIP } from "../../../utilities/constants";

// Material UI imports
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import Typography from '@mui/material/Typography';
import { FormControlLabel, IconButton, StepButton, Switch, Tooltip } from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import fileDownload from 'js-file-download';

import palette from "../../../utilities/palette";
import PreviewSection from "./PreviewSection";
import SRPInformation from "./SRPInformation";
import ModuleInformation from "./ModuleInformation";
import PreAssemblyImage from "./PreAssemblyImage";
import MachineList from "./MachineList/MachineList";
import SubmitSection from "./SubmitSection/SubmitSection";
import HomeIcon from '@mui/icons-material/Home';
import { populateMachineListErrorsSection, resetSRPFormState } from "../../../utilities/utilityFunctions";
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { importProcessDataTemplate } from "../../../models/ImportProcessData";
import { dynamicSelectOptionsMapTemplate } from "../../../models/DynamicSelectOptionsMapTemplate";
import { SelectOption } from "../../../utilities/dropDownOptions";



export default function EditForm(
    {
        textFieldValues, setTextFieldValues, 
        textFieldErrors, setTextFieldErrors, 
        selectedKeyPersonOption, setSelectedKeyPersonOption, 
        selectedResponsibleOptions, setSelectedResponsibleOptions,
        moduleInformation, setModuleInformation, 
        moduleInformationErrors, setModuleInformationErrors,
        machineList, setMachineList, 
        preassemblyImage, setPreassemblyImage, 
        preassemblyImageErrors, setPreassemblyImageErrors,
        machineListErrors, setMachineListErrors, 
        projectKey, setProjectKey, moduleName, setModuleName, 
    }: Readonly<{
        textFieldValues: StateSRPdParentLevelInterface; setTextFieldValues: Dispatch<SetStateAction<StateSRPdParentLevelInterface>>;
        textFieldErrors: any; setTextFieldErrors: Dispatch<SetStateAction<any>>; // Define the
        selectedKeyPersonOption: SelectOption; setSelectedKeyPersonOption: Dispatch<SetStateAction<SelectOption>>;
        selectedResponsibleOptions: SelectOption[]; setSelectedResponsibleOptions: Dispatch<SetStateAction<SelectOption[]>>;
        moduleInformation: SRPDModuleInformationInterface; setModuleInformation: Dispatch<SetStateAction<SRPDModuleInformationInterface>>;
        moduleInformationErrors: any; setModuleInformationErrors: Dispatch<SetStateAction<any>>; // Define the type according to your needs
        machineList: StateMachineListInterface[]; setMachineList: Dispatch<SetStateAction<StateMachineListInterface[]>>;
        preassemblyImage: SRPDPreAssemblyImageInterface; setPreassemblyImage: Dispatch<SetStateAction<SRPDPreAssemblyImageInterface>>;
        preassemblyImageErrors: any; setPreassemblyImageErrors: Dispatch<SetStateAction<any>>;
        machineListErrors: any; setMachineListErrors: Dispatch<SetStateAction<any>>; // Define the type according to your needs
        projectKey: string, moduleName: string, setProjectKey: Dispatch<SetStateAction<string>>; setModuleName: Dispatch<SetStateAction<string>>;
    }>
) {


  const { 
    architectureType, 
    ecuCataSelectObj,
    resetDataProviderState,
    ECUData, setECUData,
    setImportProcessData,
    dynamicSelectOptionsMap,
    setDynamicSelectOptionsMap,
    signingAllowedModules
  } = useDataContext();

  const [stepsValidationResult, setStepsValidationResult] = useState([true, true, true, true, true]);
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState([false, false, false, false, false, false]);
  const [isFormValid, setIsFormValid] = useState(false);


  // Tracks if the "complete" button has been clicked
  const [completeButtonClicked, setCompleteButtonClicked] = useState(false);
  const [dryRun, setDryRun] = useState(false);


  // 
  const [isResetButtonClicked, setIsResetButtonClicked] = useState(false);


  // track completed steps

  useEffect (() => {
    console.log("Completed steps: " + JSON.stringify(completed));
  }, [completed]);

const stepLabels = ["SRP information", "Module Information", "Machine Information", "Pre Assembly Image", "Preview"];

useEffect(() => {
  const isTextFieldValid = softValidateTextFieldValues(textFieldErrors);
  const isModuleInformationValid = softValidateModuleInformation(moduleInformationErrors);
  const isMachineListValid = softValidateMachineList(machineListErrors);

  setStepsValidationResult((prevStepsValidationResult) => {
    const newStepsValidationResult = [...prevStepsValidationResult];
    newStepsValidationResult[0] = isTextFieldValid;
    newStepsValidationResult[1] = isModuleInformationValid;
    newStepsValidationResult[2] = isMachineListValid;
    return newStepsValidationResult;
  });
}, [textFieldErrors, machineListErrors, moduleInformationErrors]);

const getStepContent = (step) => {
  switch (step) {
    case 0:
      return (
      <SRPInformation
            textFieldValues={textFieldValues} 
            setTextFieldValues={setTextFieldValues} 
            textFieldErrors={textFieldErrors}
            setTextFieldErrors={setTextFieldErrors} 
            selectedKeyPersonOption={selectedKeyPersonOption}
            selectedResponsibleOptions={selectedResponsibleOptions}
            setSelectedResponsibleOptions={setSelectedResponsibleOptions} 
            setSelectedKeyPersonOption={setSelectedKeyPersonOption}
            moduleInformation={moduleInformation}
            setProjectKey={setProjectKey}
            setModuleName={setModuleName}
        />    
    );
    case 1:
      return (
      <ModuleInformation
        moduleInformation={moduleInformation}
        setModuleInformation={setModuleInformation}
        moduleInformationErrors={moduleInformationErrors}
        setModuleInformationErrors={setModuleInformationErrors}
        setModuleName={setModuleName}
        setMachineList={setMachineList}
        setMachineListErrors={setMachineListErrors}
    />
);
    case 2:
      return (
      <MachineList
        moduleInformation={moduleInformation}
        machineList={machineList}
        setMachineList={setMachineList}
        machineListErrors={machineListErrors}
        setMachineListErrors={setMachineListErrors}
        projectKey={projectKey}
        moduleName={moduleName}

    />
);
    case 3:
      return (
      <PreAssemblyImage
        preassemblyImage={preassemblyImage}
        setPreassemblyImage={setPreassemblyImage}
        preassemblyImageErrors={preassemblyImageErrors}
        setPreassemblyImageErrors={setPreassemblyImageErrors}
    />
);
    case 4:
      return <PreviewSection
        getJsonData={getJsonContent}
    />;
  }

  return "Unknown step.s An error has occurred. Please contact the developer.";
};

  useEffect(() => {
    // Save ecuCataSelectObj to local storage when it changes
    saveDataToLocalStorage('ecuCataSelectObj', ecuCataSelectObj);
  }, [ecuCataSelectObj]);


  // returns raw compiled json without valiation/checks that would stop the return
  const getJsonContent = () => {

    let extraSRPRelatedContent = {};
    const newMachineList = structuredClone(machineList);
      let vadrnivsInformation = [];

      for (const machine of newMachineList) {
        for (const variant of machine.variantList) {
          // Remove the supplier property from the variant object, since it is not in the schema
          if (variant.hasOwnProperty("supplier")) {
            delete variant.supplier;
          }
          for (const cluster of variant.clusterList) {
            vadrnivsInformation.push({
              ...cluster.VADRnIVSInformation,
              swPartNumber: cluster.swPartNumber,
            });

            // Remove non-schema field
            delete cluster.vbfType;
            delete cluster?.VADRnIVSInformation;
            delete cluster?.clusterUrlOptions;
            delete cluster?.part2UrlOptions;
            if (cluster.hasOwnProperty("isProcessingSignFileMetadata")) {
              delete cluster.isProcessingSignFileMetadata;
            }

            if (cluster.hasOwnProperty("clusterUrlOptions")) {
              delete cluster.clusterUrlOptions;
            }
          }
        }
      }

      extraSRPRelatedContent = {
        VADRnIVSInformation: vadrnivsInformation
      };
    // }


    return {
      srpContent: {
        srpdName: textFieldValues.srpdName,
        srpdVersion: textFieldValues.srpdVersion,
        srpAssignee: selectedKeyPersonOption ? selectedKeyPersonOption.value : "",
        srpWatchers: selectedResponsibleOptions.map((option) => option.value),
        srpSchemaVersion: currentSRPSchemaVersion,
        releaseObjectKey: textFieldValues.releaseObjectKey,
        buildNumber: "",
        fixVersion: textFieldValues.fixVersion.trim(),
        // Include every distinct architecture among selected programs (e.g. both FNV3.2 and FNV3.4), falling back to the single detected type.
        electricalArchitecture: ECUData.current.programData?.electricalArchitectures?.length
          ? ECUData.current.programData.electricalArchitectures
          : [architectureType],
        moduleInformation: moduleInformation,
        machineList: newMachineList,
        preassemblyImage: preassemblyImage
      },
      extraSRPRelatedContent: extraSRPRelatedContent,
    }
  };


  // Combines the data with checks/validations. Used in the submit process.
    // if object with "error" is returned, submit process stops. Otherwise it is expected "jsonContent" in the object
  const buildJSON = async () => {

    // Checks if required fields meet the form's validation, returning an error if not
    if(!validateFormAndGetIsValid({
        textFieldErrors, textFieldValues, setTextFieldErrors, moduleInformation, setModuleInformationErrors, machineList, setMachineListErrors, architectureType, vehicleProgramOptions: dynamicSelectOptionsMap.vehiclePrograms, allowSigningModules: signingAllowedModules})){
      return { error: "requiredFieldMissing" };
    }

    try {
      const jsonContent = getJsonContent();
      return jsonContent
    } catch (e) {
      console.log(`Submit Error: ${e}`);

      return { error: "failedBuild" };
    }
  };

  // ===================== Stepper =====================

  // Utility functions for making code more readable  
  function allStepsCompleted(){ return completed.every((step) => step === true); }
  function isLastStep(){ return activeStep === stepLabels.length - 1; }


  // Handles clicking on another step to traverse to that step
  const handleStep = (step) => {
    validateActiveStep();
    const newCompleted = [...completed];
    newCompleted[activeStep] = true
    setCompleted(newCompleted);
    setActiveStep(activeStep === step ? -1 : step);
  };



  // Handles the clicking of the "next/complete" button
  const handleComplete = () => {
    console.log("textFieldValues: ", textFieldValues);
    const isFormValid = validateFormAndGetIsValid({
        textFieldErrors, textFieldValues, setTextFieldErrors, 
        moduleInformation, setModuleInformationErrors, 
        machineList, setMachineListErrors, 
        architectureType,
        vehicleProgramOptions: dynamicSelectOptionsMap.vehiclePrograms,
        allowSigningModules: signingAllowedModules
    });

    console.log("Form is valid: ", isFormValid);
    setStepsValidationResult(isFormValid.validationResults);

    // Set all steps to be completed
    const newCompleted = structuredClone(completed);
    newCompleted.fill(true);
    setCompleted(newCompleted);
    setCompleteButtonClicked(true);


    // if not all steps are validated, set active step to the first step that is not validated
    if (!isFormValid.isValid) {
      for (let i = 0; i < isFormValid.validationResults.length; i++) {
        if (!isFormValid.validationResults[i]) {
          setActiveStep(i);
          return false;
        }
      }
    }

    setActiveStep(-1);
    setIsFormValid(isFormValid.isValid);
    return true;
  };

  // Fetches the validaton associated with the current active step and runs it.
  const validateActiveStep = () => {
    const newStepsValidationResult = structuredClone(stepsValidationResult);
    newStepsValidationResult[activeStep] = validateStep(activeStep);
    setStepsValidationResult(newStepsValidationResult);
  };

  const validateStep = (step) => {
    switch(step){
      case 0:
        return validateSRPInformation({textFieldErrors, textFieldValues, setTextFieldErrors, architectureType, moduleName: moduleInformation.moduleName,vehicleProgramOptions: dynamicSelectOptionsMap.vehiclePrograms});
      case 1:
        return validateModuleInformation({moduleInformation, setModuleInformationErrors, architectureType});
      case 2:
        return validateMachineList({machineList, setMachineListErrors, architectureType, allowSigningModules: signingAllowedModules, moduleName: moduleInformation.moduleName});
      case 3:
        return true;
      default:
        return true;
    }
  }

  // Helper function to determine visibility icon color
  const getVisibilityIconColor = (index) => {
    if (completed[index]) {
      return 'green';
    }
    
    if (activeStep === index) {
      return palette.primary.text;
    }
    
    return '#c9c9c9';
  };

  // Displays different icons based on step's status. Returns the step's number by default.
  function calculateIconForStep(index) {
    // if it's the 6th step, show an eye icon for 'preview'
    if(index === 5)
      return <VisibilityIcon
        sx={{
          color: getVisibilityIconColor(index),
          fontSize: "2rem",
        }}
      />; 

    // if the step failed to validate, show red 'x' icon
    if(!stepsValidationResult[index]){
      return <CancelIcon style={{color: palette.stepper.error, fontSize: "2rem", borderRadius: "50%", border: `2px solid ${palette.stepper.error}`}} />;
    }

    return completed[index] ? 
      <CheckCircleIcon sx={{color: "green", fontSize: "2rem", borderRadius: "50%", border: `2px solid green`}} />
      : 
        // construct a circle with the step number
        <div style={{
          color: `white`,
          fontSize: "1.05rem",
          borderRadius: "50%",
          backgroundColor: index === activeStep ? palette.primary.main :`#c9c9c9`,
          border: `2px solid inherit`,
          width: "2rem",
          height: "2rem",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontFamily: "FordF-1-Regular",
        }}>
          {index + 1}
        </div>
  }

  // Helper function to determine step button styling
  const getStepButtonStyle = () => {
    if (activeStep === 6) {
      return { color: `${palette.primary.main}`, fontSize: "3rem" };
    }
    
    if (allStepsCompleted()) {
      return { color: `${palette.stepper.main}`, fontSize: "3rem" };
    }
    
    return { color: `${palette.primary.exampleText}`, fontSize: "3rem" };
  };


  // Resets all states to their initial values
    // Potentially make a state flag that each component listens for change, if it changes, reset
  const handleHomeRedirect = () => {
    console.log("Triggered handleHomeRedirect");
    // Stepper States
    setActiveStep(0);
    setCompleted([false, false, false, false, false]);
    setStepsValidationResult([true, true, true, true, true]);
    setCompleteButtonClicked(false);

    // Data States
    resetSRPFormState({
      setProjectKey,
      setModuleName,
      setMachineList,
      setModuleInformation,
      setPreassemblyImage,
      setPreassemblyImageErrors,
      setSelectedKeyPersonOption,
      setSelectedResponsibleOptions,
      setTextFieldValues,
      setTextFieldErrors,
      setMachineListErrors,
      setModuleInformationErrors,
    });
    setDynamicSelectOptionsMap((prev) => ({
      ...structuredClone(dynamicSelectOptionsMapTemplate),
      supplier: prev.supplier, // retain supplier options
    }));

    setImportProcessData((prev) => structuredClone(importProcessDataTemplate));
  
    // Submit States
    setIsFormValid(false);

    removeDataFromLocalStorage();

    // DataProvider States
    resetDataProviderState();
  };

  const handleReset = () => {
    const prefilledJSONContent = ECUData.current.prefilledJSONContentBackup;
    prefilledJSONContent.preassemblyImage.clusterList = [];

    setECUData((prev) => ({
      ...prev,
      prefilledJSONContent: structuredClone(prev.prefilledJSONContentBackup)
    }))

    setTextFieldValues({
      srpdName: prefilledJSONContent.srpdName || "",
      srpdVersion: prefilledJSONContent.srpdVersion || "",
      electricalArchitecture: prefilledJSONContent.electricalArchitecture || [],
      srpAssignee: "",
      srpWatchers: [],
      srpSchemaVersion: currentSRPSchemaVersion,
      releaseObjectKey: "",
      fixVersion: "",
    });

    setSelectedKeyPersonOption(undefined);
    setSelectedResponsibleOptions([]);

    setModuleInformation(prefilledJSONContent.moduleInformation);
    setMachineList(prefilledJSONContent.machineList);
    setPreassemblyImage(prefilledJSONContent.preassemblyImage);

    // Stepper States
    setActiveStep(0);
    setCompleted([false, false, false, false, false]);
    setStepsValidationResult([true, true, true, true, true]);
    setCompleteButtonClicked(false);

    // Errors
    setTextFieldErrors({});
    setModuleInformationErrors({});
    setPreassemblyImageErrors({});

    populateMachineListErrorsSection(prefilledJSONContent.machineList, setMachineListErrors);

    setIsResetButtonClicked(true);
    
    // Submit States
    setIsFormValid(false);

    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  
  // ===================================================
  return (
        <div style={{fontFamily: "FordF-1-Regular", height: '100%'}}>
          {/* {!showPrefill && ( */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexDirection: 'row', margin: '20px', flex: '1 1 0' }}>
              <div style={{display: 'flex', flex: '2 1'}}>
                <Tooltip title="Go to Home" >
                    <IconButton
                        onClick={() => {
                            localStorage.clear();
                            handleHomeRedirect();
                        }}
                    >
                        <HomeIcon sx={{
                            fontSize: '48px',
                            color: palette.main,
                            justifySelf: 'start',
                            ":hover": {
                                color: palette.hover
                            }
                        }} />

                    </IconButton>
                </Tooltip>
                <Tooltip title={dryRun? "Readiness check mode is on" : "Readiness check mode is off"} describeChild>
                <FormControlLabel 
                    control={
                    <Switch
                      checked={dryRun}
                      onChange={() => {
                        if (dryRun) {
                          setDryRun(false);
                        } else {
                          setDryRun(true);
                        }
                      }}
                      name="dryRun"
                      sx={{
                        '& .MuiSwitch-track': {
                          backgroundColor: palette.primary.main,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: palette.main,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: palette.primary.main,
                        },


                      }}
                      size="medium"

                    />
                  } 
                  labelPlacement="top"
                  label="Readiness Check Mode"  
                  sx={{justifySelf: 'start', alignItems: 'flex-start'}}
                  slotProps={{
                    typography: {
                      sx: {
                        fontSize: "16px",
                        color: palette.main,
                        fontWeight: '600'
                      }
                    }
                  }}
                />
                </Tooltip>
              </div>

              <div style={{ display: "flex", alignItems: "center", flexDirection: 'row', flex: '1 1', justifyContent: 'flex-end' }}>

                  <span style={{marginRight: '10px'}}>
                    <Tooltip title="Download SRPd">
                      <IconButton
                        onClick={() => {
                          // Download the SRPd
                          const { srpContent } = getJsonContent();
                          const data = srpContent;
                          const blob = new Blob([JSON.stringify(data, null, '   ')], {type: 'application/json'});
                          const fileName = (data.srpdName && data.srpdVersion) ? `${data.srpdName}_${data.srpdVersion}SRPd.json` : 'SRPd.json';
                          fileDownload(blob, fileName);
                        }}
                      >
                        <DownloadIcon
                          sx={{
                            color: palette.main,
                            fontSize: '36px',
                            ":hover": {
                            color: palette.hover
                          }}} 
                        
                        />
                        
                      </IconButton>
                    </Tooltip>
                  </span>

                  <span style={{marginRight: '10px'}}>
                    <Tooltip title="Reset All">
                      <IconButton
                        onClick={() => {
                          // Download the SRPd
                          handleReset();
                        }}
                      >
                        <ReplayIcon 
                        sx={{
                            color: palette.main,
                            fontSize: '36px',
                            transition: 'transform 0.5s ease-in-out',                            
                            '&:hover': {
                                transform: 'rotate(-360deg)',
                                color: palette.hover,
                            }
                            }}
                            onClick={() => {
                                handleReset();
                            }}
                            />
                      </IconButton>

                    </Tooltip>
                  </span>
              </div>
            </div>
          <div 
            style={{
              margin: "20px",
            }}
            >
            <Stepper 
              orientation="vertical" 
              nonLinear 
              activeStep={activeStep}
              sx={{
                "& .MuiStepConnector-line": {
                  minHeight: '5vh',
                },
              }}
            >
              {
              stepLabels.map((step, index) => {
                return (
                  <Step key={step}
                  completed={completed[index]}
                  sx={{
                    fontFamily: "FordF-1-Regular",
                  }}
                  >
                    {/* Step label/icon */}
                    <StepButton 
                      id="stepButton"
                      onClick={
                        () => handleStep(index)
                      }    
                      icon={calculateIconForStep(index)}
                      style={getStepButtonStyle()}
                    >
                      <label style={{fontSize: '24px', cursor: 'pointer', color: palette.main, fontWeight: '600'}}>{step}</label>
                    </StepButton>
                    
                    {/* The step's internal content */}
                    <StepContent>
                      {getStepContent(index)}

                      {/* 'back' 'next' and 'reset' buttons, conditionally displayed */}
                      <div>
                          <Tooltip title="Go Back">
                            <IconButton
                                disabled={activeStep === 0 || activeStep === -1}
                                sx={{
                                    borderRadius: '20px',
                                    backgroundColor: palette.main,
                                    color: palette.primary.text, 
                                    ":hover": {
                                        backgroundColor: palette.hover,
                                    },
                                    ":disabled": {
                                      backgroundColor: palette.disabled.background
                                    },
                                    marginTop: "10px",
                                    marginRight: "10px"
                                }}
                                onClick={() => 
                                  handleStep(activeStep - 1)
                                }>                              
                                <ArrowBackIcon style={{
                                    color: "white"
                                }}/>
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={isLastStep()? "Complete" : "Go Next"}>
                              <IconButton
                                  sx={{
                                      borderRadius: '20px',
                                      backgroundColor: palette.main,
                                      color: palette.primary.text, 
                                      ":hover": {
                                          backgroundColor: palette.hover,
                                      },
                                      marginTop: "10px",
                                  }}
                                  onClick={ 
                                    isLastStep() ? handleComplete : () => handleStep(activeStep + 1)
                                  }                               >
                                  <ArrowForwardIcon style={{
                                      color: "white"
                                  }}/>
                              </IconButton>
                          </Tooltip>

                      </div>
                    </StepContent>
                  </Step>
                );
              })

              }
            </Stepper>

            
            {completeButtonClicked && (
              <Typography 
              sx={{marginTop: "20px", color: stepsValidationResult.some((step) => !step) ? palette.stepper.error : palette.stepper.main, fontWeight: 'bold'}}
              >
                {
                stepsValidationResult.some((step) => !step) ? "The form is incomplete. Please fill in the required fields." : "All steps completed. Please ensure schema validation is successful before submitting."}
              </Typography>
            )}
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
          {/* Warning if dry run is true */}
          {dryRun && (
            <Tooltip title={READINESS_CHECK_TOOLTIP} describeChild>
              <div tabIndex={0} style={{color: palette.stepper.warning, fontSize: "1.5rem", fontWeight: "bold", marginLeft: "20px", marginBottom: '10px'}}>
                Readiness check mode is enabled
              </div>
            </Tooltip>
          )}


          </div>


          <SubmitSection 
            jsonFunction={buildJSON}
            isFormValid={isFormValid}
            dryRun={dryRun}
            handleComplete={handleComplete}
            isResetButtonClicked={isResetButtonClicked}
            setIsResetButtonClicked={setIsResetButtonClicked}
          />
        </div>
  );
}